import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../api/auth';
import toast from 'react-hot-toast';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { getUserTimeZone } from '../utils/dateUtils';

const AuthContext = createContext();

const firebaseConfig = {
  apiKey: "AIzaSyDhFhTkSHbadYW-d1dH2A8e52V4-40qbE0",
  authDomain: "saas-823ac.firebaseapp.com",
  projectId: "saas-823ac",
  storageBucket: "saas-823ac.firebasestorage.app",
  messagingSenderId: "806168481737",
  appId: "1:806168481737:web:0f7ba368d8a42b59d76218",
  measurementId: "G-NDKJ2KCHYJ"
};

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BCYKudJpj_L7o16jST3gTbscLcb9aVzZwrj7_hPOffcIandbBjPbHhW7i41FzAKk1zaofo_9bq6Qqhw3yl2qsh8';

const ensureFirebaseApp = () => {
  if (getApps().length > 0) return getApps()[0];
  return initializeApp(firebaseConfig);
};

const getWebDeviceToken = async () => {
  try {
    if (!('serviceWorker' in navigator)) return null;
    if (!('Notification' in window)) return null;

    const supported = await isSupported();
    if (!supported) return null;

    if (Notification.permission !== 'granted') return null;

    const app = ensureFirebaseApp();
    const messaging = getMessaging(app);
    const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
    return token || null;
  } catch (e) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data.data);
    } catch (error) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const device_token = await getWebDeviceToken();
    const response = await authAPI.login({
      email,
      password,
      ...(device_token ? { device_token, device_platform: 'web' } : {})
    });
    const { accessToken, refreshToken, user } = response.data.data;
    
    // Clear admin tokens to avoid session confusion
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    
    setUser(user);
    toast.success('Login successful!');
    return user;
  };

  const register = async (userData) => {
    const device_token = await getWebDeviceToken();
    const timezone = getUserTimeZone();
    let payload;
    
    if (userData instanceof FormData) {
      payload = userData;
      if (timezone && !payload.has('timezone')) {
        payload.append('timezone', timezone);
      }
      if (device_token) {
        payload.append('device_token', device_token);
        payload.append('device_platform', 'web');
      }
    } else {
      payload = device_token
        ? { ...userData, timezone, device_token, device_platform: 'web' }
        : { ...userData, timezone };
    }

    let response;
    if ((userData instanceof FormData && userData.has('user_type')) || payload.user_type) {
      response = await authAPI.registerByType(payload);
    } else {
      response = await authAPI.register(payload);
    }

    const { accessToken, refreshToken, user, requires_approval } = response.data.data;
    
    if (requires_approval) {
      toast.success('Registration submitted! Pending admin approval.');
      return { requires_approval: true };
    }
    
    // Clear admin tokens to avoid session confusion
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
    }
    
    toast.success('Registration successful!');
    return { ...user, requires_approval: false };
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data) => {
    const response = await authAPI.updateMe(data);
    const updatedUser = response.data.data;
    if (updatedUser) {
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    toast.success('Profile updated successfully');
    return updatedUser;
  };

  const changePassword = async (current_password, new_password) => {
    const response = await authAPI.changePassword({ current_password, new_password });
    toast.success('Password updated successfully');
    return response.data.data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    updateProfile,
    changePassword,
    logout,
    isAuthenticated: !!user,
    isBusinessOwner: user?.role === 'business_owner',
    isStaff: user?.role === 'staff',
    isCustomer: user?.role === 'customer',
    isAdmin: user?.role === 'super_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
